// 🔴 P0 - SIMPLIFICATION RADICALE : AuditTab.jsx
// Flux unique : Upload → Analyse IA → Plan d'Action (AUCUNE page intermédiaire)
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, Brain, Shield, CheckCircle, Phone, Zap,
  Loader2, ChevronDown, RotateCcw, ArrowRight, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
// 🔴 P0 : Import direct du Plan d'Action (AUCUNE page intermédiaire)
import { ActionPlanView } from '../ActionPlanView';
// Import de l'étape Documents
import { DocumentCollectionStep } from '../DocumentCollectionStep';

// Configuration des 10 catégories d'audit
const AUDIT_CATEGORIES = [
  { id: "telephonie", name: "Téléphonie / Internet", icon: Phone, color: "#3B82F6" },
  { id: "streaming", name: "Streaming", color: "#EF4444" },
  { id: "energie", name: "Énergie", icon: Zap, color: "#F59E0B" },
  { id: "cloud", name: "Logiciels / Cloud", color: "#06B6D4" },
  { id: "sport", name: "Sport / Bien-être", color: "#10B981" },
  { id: "banque", name: "Frais Bancaires", color: "#64748B" },
];

// 🔴 P0 - SIMPLIFICATION RADICALE : Composant Principal
const AuditTab = ({ documents, activeSubscription, onDocumentUploaded }) => {
  // États simplifiés - flux linéaire uniquement
  const [scanning, setScanning] = useState(false);
  const [detectedSubs, setDetectedSubs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [showCgvError, setShowCgvError] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // 🔴 P0 : États pour le flux SIMPLIFIÉ (One-Way Ticket)
  const [currentView, setCurrentView] = useState('upload'); // 'upload' | 'scanning' | 'action-plan' | 'documents'
  const [savedAuditChoices, setSavedAuditChoices] = useState([]);
  
  // 🎫 TICKET #02 : L'audit est INCLUS dans le Pack Déménagement
  // Liste des packs qui donnent accès à l'audit gratuitement
  // 🎫 SILO UNITAIRE #01 : L'audit reste accessible pour les packs audit uniquement
  // Le tunnel de déménagement est maintenant isolé et ne donne plus accès automatiquement à l'audit
  const PACKS_WITH_FREE_AUDIT = [
    'audit_pouvoir_achat',
    'mission_audit'
  ];
  
  const hasAuditPack = activeSubscription && (
    PACKS_WITH_FREE_AUDIT.includes(activeSubscription.pack_type) ||
    activeSubscription.pack_name?.toLowerCase().includes('audit') ||
    activeSubscription.pack_name?.toLowerCase().includes('déménagement') ||
    activeSubscription.pack_name?.toLowerCase().includes('depart')
  );
  
  // 🔴 P0 : Charger les subscriptions existantes au montage
  useEffect(() => {
    fetchDetectedSubs();
  }, []);
  
  // 🔴 P0 TICKET #01 : FORCER le Plan d'Action dès que des subs existent
  // Suppression de TOUTE page intermédiaire
  useEffect(() => {
    if (detectedSubs.length > 0) {
      // FORCER immédiatement le Plan d'Action - AUCUNE exception
      if (currentView !== 'action-plan' && currentView !== 'documents') {
        setCurrentView('action-plan');
      }
    }
  }, [detectedSubs, currentView]);
  
  const fetchDetectedSubs = async () => {
    try { 
      const token = localStorage.getItem('token'); 
      const res = await axios.get(`${API}/ai/detected-subscriptions`, { 
        headers: { 'Authorization': `Bearer ${token}` }, 
        withCredentials: true 
      }); 
      const subs = res.data.subscriptions || [];
      setDetectedSubs(subs);
      // 🔴 TICKET #01 : Forcer immédiatement si des subs sont trouvées
      if (subs.length > 0) {
        setCurrentView('action-plan');
      }
    } catch (err) {
      console.log('Erreur fetch subscriptions:', err);
    }
  };
  
  // 🔴 P0 : Handler pour l'upload et scan IA
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setCurrentView('scanning'); // Afficher l'animation de scan
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      // 1. Upload du document
      const uploadRes = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      if (uploadRes.data.document_id) {
        toast.success('Document uploadé ! Analyse IA en cours...');
        setScanning(true);
        
        // 2. Scan IA du document
        const scanRes = await axios.post(`${API}/ai/parse-document/${uploadRes.data.document_id}`, {}, { 
          headers: { 'Authorization': `Bearer ${token}` }, 
          withCredentials: true 
        });
        
        const count = scanRes.data.count || 0;
        toast.success(`${count} abonnement(s) détecté(s) !`);
        
        // 3. Rafraîchir les subscriptions
        await fetchDetectedSubs();
        
        // 4. Callback parent si fourni
        if (onDocumentUploaded) onDocumentUploaded();
        
        // 🔴 P0 : FLUX DIRECT → Plan d'Action (pas de page intermédiaire)
        setCurrentView('action-plan');
      }
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
      setCurrentView('upload'); // Retour à l'upload en cas d'erreur
    } finally { 
      setUploading(false); 
      setScanning(false); 
    }
  };
  
  // Handler pour le drag & drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileUpload(e.dataTransfer.files[0]);
  };
  
  // 🔴 P0 : Handler pour souscrire au pack Audit
  const handleSubscribeAudit = async () => {
    if (!cgvAccepted) {
      setShowCgvError(true);
      toast.error('Veuillez accepter les CGV pour continuer');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/payments/checkout`, 
        { 
          pack_type: 'audit_pouvoir_achat', 
          origin_url: window.location.origin,
          cgv_accepted: cgvAccepted,
          cgv_accepted_at: new Date().toISOString()
        },
        { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true }
      );
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.admin_bypass) { 
        toast.success('Pack Audit activé !');
      }
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Erreur'); 
    } finally { 
      setProcessingPayment(false); 
    }
  };
  
  // 🔴 P0 : Handler quand le Plan d'Action est complété → Étape Documents
  const handleActionPlanComplete = (auditChoices) => {
    if (auditChoices && auditChoices.length > 0) {
      setSavedAuditChoices(auditChoices);
    }
    setCurrentView('documents');
    toast.success('Plan d\'action validé ! Passons aux documents.');
  };
  
  // 🔴 P0 : Handler quand les documents sont validés → Paiement
  const handleDocumentsComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/payments/create-cancellation-invoice`, 
        { 
          selected_subscriptions: savedAuditChoices
            .filter(c => c.choice === 'resilier')
            .map(c => c.detection_id),
          estimated_savings: savedAuditChoices
            .filter(c => c.choice === 'resilier')
            .reduce((sum, c) => sum + (parseFloat(c.amount) || 0) * 12, 0)
        }, 
        { headers: { 'Authorization': `Bearer ${token}` }, withCredentials: true }
      );
      
      if (res.data.checkout_url) { 
        toast.info('Redirection vers le paiement...'); 
        window.location.href = res.data.checkout_url; 
      }
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Erreur'); 
    }
  };
  
  // 🔴 P0 : Handler pour le nouvel audit (reset complet)
  const handleNewAudit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Supprimer les données côté serveur
      await axios.delete(`${API}/audit-actions/reset-audit`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      // Supprimer les abonnements détectés
      await axios.delete(`${API}/ai/detected-subscriptions/reset`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      // Réinitialiser l'état local
      setDetectedSubs([]);
      setSavedAuditChoices([]);
      setCurrentView('upload');
      toast.success('Audit réinitialisé. Uploadez un nouveau relevé bancaire.');
    } catch (error) {
      console.log('Erreur reset audit:', error);
      // Même en cas d'erreur, réinitialiser l'état local
      setDetectedSubs([]);
      setSavedAuditChoices([]);
      setCurrentView('upload');
      toast.info('Nouvel audit initialisé.');
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🔴 P0 : RENDU CONDITIONNEL SIMPLIFIÉ (One-Way Ticket)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // VUE : Étape Documents
  if (currentView === 'documents') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => setCurrentView('action-plan')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowRight className="w-4 h-4 rotate-180 mr-2" />
            Retour au Plan d'Action
          </Button>
          <div className="text-sm text-cyan-400 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-400/20 flex items-center justify-center text-xs">2</span>
            Étape 2/3 : Documents
          </div>
        </div>
        
        <DocumentCollectionStep 
          auditChoices={savedAuditChoices}
          hasDemenagement={false}
          onComplete={handleDocumentsComplete}
          onBack={() => setCurrentView('action-plan')}
        />
      </div>
    );
  }
  
  // VUE : Plan d'Action (DESTINATION PRINCIPALE après scan)
  if (currentView === 'action-plan' && detectedSubs.length > 0) {
    return (
      <div className="space-y-6">
        {/* Header avec bouton Nouvel Audit */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="font-orbitron text-xl md:text-2xl font-bold text-white flex items-center gap-2">
              <Brain className="w-6 h-6 text-success" />
              Plan d'Action Personnalisé
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {detectedSubs.length} abonnement(s) analysé(s) • Cliquez sur un contrat pour voir les détails
            </p>
          </div>
          
          <Button 
            onClick={handleNewAudit}
            variant="outline"
            className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10"
            data-testid="new-audit-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Nouvel Audit
          </Button>
        </div>
        
        {/* 🔴 P0 FIX : ActionPlanView simplifié - Plus de modale */}
        <ActionPlanView 
          auditResults={{ subscriptions: detectedSubs }}
          onComplete={handleActionPlanComplete}
        />
      </div>
    );
  }
  
  // VUE : Animation de Scan IA
  if (currentView === 'scanning') {
    return (
      <div className="space-y-6">
        <div className="glass-card p-12 border border-cyan-400/30 text-center">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 360]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-cyan-400/20 flex items-center justify-center"
          >
            <Brain className="w-12 h-12 text-cyan-400" />
          </motion.div>
          
          <h3 className="font-orbitron text-2xl font-bold text-white mb-4">
            Analyse IA en cours...
          </h3>
          
          <div className="flex items-center justify-center gap-2 mb-6">
            <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
            <span className="text-cyan-400 font-mono text-sm">
              {scanning ? 'Détection des abonnements...' : 'Upload du document...'}
            </span>
          </div>
          
          <div className="max-w-md mx-auto space-y-2">
            {['Lecture du relevé', 'Identification des créanciers', 'Classification par catégorie', 'Calcul des économies'].map((step, i) => (
              <motion.div 
                key={step}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.5 }}
                className="flex items-center gap-3 text-sm"
              >
                <CheckCircle className="w-4 h-4 text-success" />
                <span className="text-gray-400">{step}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // VUE : Upload (vue par défaut)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-orbitron text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <Brain className="w-6 h-6 text-success" />
            Audit Pouvoir d'Achat
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Uploadez votre relevé → Notre IA analyse → Vous décidez
          </p>
        </div>
        
        {/* Bouton Nouvel Audit si des subs existent déjà */}
        {detectedSubs.length > 0 && (
          <Button 
            onClick={() => setCurrentView('action-plan')}
            className="bg-success text-black hover:bg-success/90 font-orbitron"
            data-testid="view-action-plan-btn"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Voir mon Plan d'Action
          </Button>
        )}
      </div>
      
      {/* Pack Audit - Afficher si pas de pack actif */}
      {!hasAuditPack && (
        <div className="glass-card p-6 border border-success/30 bg-gradient-to-br from-success/5 to-cyan-400/5 relative">
          <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-amber-400 text-black text-xs font-bold py-1 px-4 rounded-bl-lg rounded-tr-lg shadow-lg z-10">
            LE PLUS RENTABLE
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-success/20 text-success text-xs font-bold mb-4">
                <Zap className="w-3 h-3" />POPULAIRE
              </div>
              
              <h3 className="font-orbitron text-2xl font-bold text-white mb-3">
                Pack Audit - 0€ à l'inscription
              </h3>
              
              <p className="text-gray-400 mb-4">
                Notre IA analyse votre relevé et classe vos abonnements en <strong className="text-success">10 catégories</strong>.
                Vous ne payez que <strong className="text-success">20% des économies réelles</strong>.
              </p>
              
              <div className="space-y-2 mb-6">
                {[
                  "Analyse IA instantanée de vos prélèvements",
                  "Plan d'action personnalisé",
                  "Commission plafonnée à 299€ max",
                  "Promesse de neutralité"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={handleSubscribeAudit} 
                disabled={processingPayment || !cgvAccepted}
                className={`w-full md:w-auto py-4 px-8 bg-success text-black hover:bg-success/90 font-orbitron transition-all ${
                  cgvAccepted && !processingPayment ? 'animate-pulse' : !cgvAccepted ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="subscribe-audit-btn"
              >
                {processingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Commencer l'Audit<ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
              
              {/* CGV Checkbox */}
              <div className={`mt-4 p-4 rounded-lg border transition-all ${
                showCgvError && !cgvAccepted 
                  ? 'border-red-500/50 bg-red-500/5' 
                  : cgvAccepted 
                    ? 'border-success/30 bg-success/5' 
                    : 'border-gray-700 bg-gray-800/30'
              }`}>
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="audit-cgv-accept"
                    checked={cgvAccepted}
                    onCheckedChange={(checked) => {
                      setCgvAccepted(checked);
                      if (checked) setShowCgvError(false);
                    }}
                    className={showCgvError && !cgvAccepted ? 'border-red-500' : ''}
                    data-testid="audit-cgv-checkbox"
                  />
                  <label htmlFor="audit-cgv-accept" className="text-sm text-gray-300 leading-relaxed cursor-pointer">
                    J'accepte les{' '}
                    <Link to="/cgv" target="_blank" className="text-cyan-400 hover:underline">
                      Conditions Générales de Vente
                    </Link>{' '}
                    et je demande l'exécution immédiate de la prestation.
                  </label>
                </div>
                {showCgvError && !cgvAccepted && (
                  <div className="flex items-center gap-2 mt-3 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>Vous devez accepter les CGV pour continuer</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Aperçu catégories */}
            <div className="hidden md:block">
              <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-800">
                <p className="text-gray-500 text-xs text-center mb-3">10 catégories analysées</p>
                <div className="grid grid-cols-2 gap-2">
                  {AUDIT_CATEGORIES.map(cat => {
                    const CatIcon = cat.icon || Phone;
                    return (
                      <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded">
                        <CatIcon className="w-4 h-4" style={{ color: cat.color }} />
                        <span className="text-gray-300 text-xs truncate">{cat.name}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-center text-gray-500 text-xs mt-2">+4 autres catégories</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Zone Upload (toujours visible si pack actif ou en mode upload) */}
      {(hasAuditPack || true) && (
        <>
          {/* Tutoriel */}
          <div className="glass-card border border-cyan-400/20 overflow-hidden">
            <button 
              onClick={() => setShowTutorial(!showTutorial)}
              className="w-full p-3 flex items-center justify-between hover:bg-gray-800/30"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">Comment préparer votre relevé ?</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-cyan-400 transition-transform ${showTutorial ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {showTutorial && (
                <motion.div 
                  initial={{ height: 0 }} 
                  animate={{ height: 'auto' }} 
                  exit={{ height: 0 }} 
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 grid md:grid-cols-2 gap-3">
                    <div className="bg-red-500/10 p-3 rounded border border-red-500/20">
                      <p className="text-red-400 font-bold text-xs mb-2">❌ Masquez</p>
                      <ul className="text-gray-400 text-xs space-y-1">
                        <li>• N° compte / IBAN</li>
                        <li>• Solde</li>
                        <li>• Achats personnels</li>
                      </ul>
                    </div>
                    <div className="bg-success/10 p-3 rounded border border-success/20">
                      <p className="text-success font-bold text-xs mb-2">✓ Gardez visible</p>
                      <ul className="text-gray-400 text-xs space-y-1">
                        <li>• Noms créanciers</li>
                        <li>• Montants</li>
                        <li>• Dates</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Zone de Drop */}
          <div 
            className={`glass-card p-8 border-2 border-dashed transition-all ${
              dragActive ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-700'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }} 
            onDragLeave={() => setDragActive(false)} 
            onDrop={handleDrop}
          >
            <div className="text-center">
              {uploading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-12 h-12 animate-spin text-cyan-400" />
                  <p className="text-white">Upload en cours...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-white font-medium mb-2">Glissez votre relevé bancaire</p>
                  <p className="text-gray-500 text-sm mb-4">PDF, PNG ou JPG acceptés</p>
                  
                  <label className="cursor-pointer inline-block">
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])} 
                      data-testid="file-upload-input"
                    />
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-400 text-black font-bold rounded hover:bg-cyan-300 transition-colors">
                      <FileText className="w-4 h-4" />
                      Sélectionner un fichier
                    </span>
                  </label>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export { AuditTab };
export default AuditTab;
